// Utility: haal gebruikersnaam of voornaam uit e-mail
export function getDisplayName(user) {
  if (!user) return '';
  const fromEmail = () => {
    if (!user.email) return '';
    const local = user.email.split('@')[0].split('.')[0] || '';
    if (!local) return '';
    return local.charAt(0).toUpperCase() + local.slice(1);
  };

  const sanitizeName = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const normalized = raw.toLowerCase();
    // Guard against temporary/debug placeholders leaking to production UI.
    if (normalized === 'debug' || normalized === 'test' || normalized === 'technician debug') {
      return fromEmail();
    }
    return raw;
  };

  const username = sanitizeName(user.username);
  if (username) return username;

  const displayName = sanitizeName(user.displayName);
  if (displayName) return displayName;

  const emailName = fromEmail();
  if (emailName) return emailName;

  return '';
}

function isLikelyDebugTechnician(user) {
  const marker = `${user?.displayName || ''} ${user?.username || ''} ${user?.email || ''}`.toLowerCase();
  return marker.includes('debug') || marker.includes('test');
}
// Firebase helper voor week-technician roulatie
import { doc, getDoc, setDoc, collection, getDocs, serverTimestamp, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { deriveRoleFlags } from "./userRoles";

function normalizeBranchValue(value) {
  return String(value || '').trim().toLowerCase();
}

function matchesBranch(user, branchId) {
  if (!branchId) return true;
  const target = normalizeBranchValue(branchId);
  const candidates = [
    user?.branchId,
    user?.vestigingId,
    user?.branch,
    user?.vestiging,
    user?.branchName
  ].map(normalizeBranchValue);
  return candidates.includes(target);
}

// Haal de huidige week-technician op uit Firestore
// Optioneel per vestiging via branchId (zodat elke vestiging een eigen week-technician heeft)
export async function getCurrentWeekTechnician(branchId) {
  const settingsId = branchId ? `week_technician_${branchId}` : "week_technician";
  const ref = doc(db, "settings", settingsId);
  const snap = await getDoc(ref);
  if (snap.exists()) return snap.data();
  return null;
}

// Zet de week-technician in Firestore
export async function setCurrentWeekTechnician(user, branchId) {
  const settingsId = branchId ? `week_technician_${branchId}` : "week_technician";
  const ref = doc(db, "settings", settingsId);
  await setDoc(ref, {
    user,
    updatedAt: serverTimestamp()
  });
}

// Haal alle technicians op uit de users-collectie, optioneel gefilterd op branchId
export async function getTechnicianList(branchId) {
  if (!branchId) {
    const usersSnap = await getDocs(collection(db, "users"));
    return usersSnap.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(u => deriveRoleFlags(u).technician === true);
  }

  // Support legacy and current schemas where vestiging/branch can be stored under different field names.
  const fieldNames = ["branchId", "vestigingId", "branch", "vestiging", "branchName"];
  const byId = new Map();

  for (const field of fieldNames) {
    try {
      const q = query(collection(db, "users"), where(field, "==", branchId));
      const snap = await getDocs(q);
      snap.docs.forEach((d) => {
        const user = { id: d.id, ...d.data() };
        if (deriveRoleFlags(user).technician === true) byId.set(user.id, user);
      });
    } catch {
      // Ignore missing index / unsupported field errors and continue with other field names.
    }
  }

  // Fallback: one full scan to handle case/spacing differences and mixed data formats.
  if (byId.size === 0) {
    const usersSnap = await getDocs(collection(db, "users"));
    usersSnap.docs.forEach((d) => {
      const user = { id: d.id, ...d.data() };
      if (deriveRoleFlags(user).technician === true && matchesBranch(user, branchId)) {
        byId.set(user.id, user);
      }
    });
  }

  const list = Array.from(byId.values());
  list.sort((a, b) => {
    const aDebug = isLikelyDebugTechnician(a);
    const bDebug = isLikelyDebugTechnician(b);
    if (aDebug !== bDebug) return aDebug ? 1 : -1;
    return getDisplayName(a).localeCompare(getDisplayName(b), 'nl', { sensitivity: 'base' });
  });
  return list;
}

// Bepaal de volgende technician (roulatie)
// Optioneel per vestiging via branchId
export async function rotateWeekTechnician(branchId) {
  const techs = await getTechnicianList(branchId);
  if (!techs.length) return null;
  const current = await getCurrentWeekTechnician(branchId);
  let idx = 0;
  if (current && current.user && current.user.id) {
    idx = techs.findIndex(t => t.id === current.user.id);
    idx = (idx + 1) % techs.length;
  }
  const next = techs[idx];
  await setCurrentWeekTechnician(next, branchId);
  return next;
}

// Force rotation - manually rotate to next technician (top to bottom, simple order)
export async function forceRotateWeekTechnician(branchId) {
  const techs = await getTechnicianList(branchId);
  if (!techs.length) return null;

  const current = await getCurrentWeekTechnician(branchId);
  let nextIdx = 0;

  if (current && current.user && current.user.id) {
    // Find current technician's index
    const currentIdx = techs.findIndex(t => t.id === current.user.id);
    if (currentIdx !== -1) {
      // Move to next in list (wrap around)
      nextIdx = (currentIdx + 1) % techs.length;
    }
  }

  const nextTech = techs[nextIdx];
  await setCurrentWeekTechnician(nextTech, branchId);
  return nextTech;
}
